/**
 * App entry: mounts React, registers the service worker, boots the store and
 * starts sync, and owns the router.
 *
 * The router is hash-based and about sixty lines. A history router would need
 * the server to serve `index.html` for every unknown path, and a QR join link
 * that 404s on a phone at a table is a worse failure than a `#` in the URL.
 *
 * Each screen area exports a plain route map and this file registers all of
 * them, so adding a screen never means editing the entry point.
 */

import "./styles.css";

import { StrictMode, useMemo, useSyncExternalStore } from "react";
import type { ComponentPropsWithRef, ComponentType, MouseEvent, ReactNode } from "react";
import { createRoot } from "react-dom/client";

import { subscribeSync, type SyncState } from "./store/sync";
import { applyLang } from "./locale.ts";
import { chooseView } from "./auth/gate.ts";
import { resolveSession, useAuthSession } from "./auth/session.ts";
import { SignedOut } from "./auth/SignedOut.tsx";

// The screen areas import `Link`/`navigate`/`useSyncState` back from this file,
// so these three edges close a cycle. It is safe because everything they reach
// for is a hoisted function declaration used at render time — no screen module
// touches a `const` of this module while it is still initialising.
import { catalogRoutes } from "./screens/catalog/routes.ts";
import { sessionRoutes } from "./screens/session/routes.ts";
import { settingsRoutes } from "./screens/settings/routes.ts";

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export type RouteParams = Readonly<Record<string, string>>;

export interface ScreenProps {
  params: RouteParams;
  query: URLSearchParams;
}

export type Screen = ComponentType<ScreenProps>;

const routes = new Map<string, Screen>();
const subscribers = new Set<() => void>();
let revision = 0;
let notFoundScreen: Screen = NotFound;

function emit(): void {
  revision += 1;
  for (const fn of subscribers) fn();
}

function normalize(path: string): string {
  const trimmed = path.split("?")[0] ?? "/";
  if (trimmed.length > 1 && trimmed.endsWith("/")) return trimmed.slice(0, -1);
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/** Register (or replace) the screen for a route pattern such as `/game/:id`. */
export function registerRoute(pattern: string, screen: Screen): void {
  routes.set(normalize(pattern), screen);
  emit();
}

export function registerRoutes(map: Record<string, Screen>): void {
  for (const [pattern, screen] of Object.entries(map)) routes.set(normalize(pattern), screen);
  emit();
}

export function setNotFoundScreen(screen: Screen): void {
  notFoundScreen = screen;
  emit();
}

/** The path portion of the current location, always starting with `/`. */
export function currentPath(): string {
  const raw = window.location.hash.replace(/^#/, "");
  return normalize(raw || "/");
}

function currentQuery(): URLSearchParams {
  const raw = window.location.hash.replace(/^#/, "");
  const at = raw.indexOf("?");
  return new URLSearchParams(at >= 0 ? raw.slice(at + 1) : "");
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  const target = `#${to.startsWith("/") ? to : `/${to}`}`;
  if (options.replace) {
    window.history.replaceState(null, "", target);
    emit();
  } else {
    window.location.hash = target.slice(1);
  }
}

function match(pattern: string, path: string): RouteParams | null {
  const p = pattern.split("/");
  const a = path.split("/");
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const value = a[i]!;
    if (seg.startsWith(":")) params[seg.slice(1)] = decodeURIComponent(value);
    else if (seg !== value) return null;
  }
  return params;
}

/**
 * Installed once, never removed, and deliberately `emit` rather than the
 * subscriber itself.
 *
 * `useSyncExternalStore` re-reads `snapshot()` when it is notified and skips the
 * render if the value is unchanged. Calling a subscriber directly on `hashchange`
 * therefore did nothing at all: the hash had moved, but `revision` had not, so
 * React compared two equal numbers and bailed out. The whole app looked frozen —
 * every link updated the URL and left the screen where it was, with no error to
 * explain it. Routing through `emit` makes `revision` the single thing that
 * changes when *anything* about the route changes, which is what `snapshot`
 * already claimed to be.
 *
 * It stays installed after the last subscriber leaves, because tearing it down
 * with the subscriber count would restore the bug for anything that unmounts and
 * remounts the router.
 */
let hashListenerInstalled = false;

function subscribe(fn: () => void): () => void {
  if (!hashListenerInstalled && typeof window?.addEventListener === "function") {
    window.addEventListener("hashchange", emit);
    hashListenerInstalled = true;
  }
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

/** `revision` bumps on every navigation and every route registration. */
function snapshot(): number {
  return revision;
}

/**
 * The router's external store, named rather than left as two loose functions so
 * the subscribe/snapshot contract can be asserted directly. The bug above was
 * invisible to any test that went through a rendered tree.
 */
export const routeStore = { subscribe, getSnapshot: snapshot };

export function useRoute(): { path: string; params: RouteParams; query: URLSearchParams } {
  const rev = useSyncExternalStore(routeStore.subscribe, routeStore.getSnapshot, () => 0);
  return useMemo(() => {
    const path = currentPath();
    for (const pattern of routes.keys()) {
      const params = match(pattern, path);
      if (params) return { path, params, query: currentQuery() };
    }
    return { path, params: {}, query: currentQuery() };
    // `rev` is the dependency: the location is not otherwise observable.
  }, [rev]);
}

function resolve(path: string): { screen: Screen; params: RouteParams } {
  for (const [pattern, screen] of routes) {
    const params = match(pattern, path);
    if (params) return { screen, params };
  }
  return { screen: notFoundScreen, params: {} };
}

/** Renders whichever screen matches the current hash. */
export function Routes(): ReactNode {
  const rev = useSyncExternalStore(subscribe, snapshot, () => 0);
  const { screen: Component, params, query } = useMemo(() => {
    const path = currentPath();
    return { ...resolve(path), query: currentQuery() };
  }, [rev]);
  return <Component params={params} query={query} />;
}

/**
 * `to` is the routing prop; `href` is derived from it. A caller-supplied `href`
 * is a type error rather than a silently accepted second source of truth — one
 * that would send the browser out of the router.
 *
 * `ComponentPropsWithRef` rather than `WithoutRef`: React 19 passes `ref`
 * through as an ordinary prop, so it rides along in `rest` onto the anchor.
 */
export type LinkProps = Omit<ComponentPropsWithRef<"a">, "href"> & {
  to: string;
  replace?: boolean;
  href?: never;
};

export function Link({ to, replace, onClick, ...rest }: LinkProps): ReactNode {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    // A caller that already cancelled the event owns the outcome: no navigation.
    if (event.defaultPrevented) return;
    // Modified clicks are the browser's (new tab, window, download): leave the
    // real `href` to do its job.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    navigate(to, { replace });
  };
  // `href` after the spread so a stray one from an untyped caller cannot win.
  return <a {...rest} href={`#${to}`} onClick={handleClick} />;
}

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

export type Theme = "light" | "dark" | "system";

/** Pins the theme, or hands control back to the OS. Wins over the media query. */
export function setTheme(theme: Theme): void {
  if (theme === "system") {
    delete document.documentElement.dataset.theme;
    localStorage.removeItem("pb-theme");
  } else {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("pb-theme", theme);
  }
}

export function getTheme(): Theme {
  const pinned = document.documentElement.dataset.theme;
  return pinned === "dark" || pinned === "light" ? pinned : "system";
}

/** Live sync state for the always-visible unsynced indicator. */
export function useSyncState(): SyncState {
  return useSyncExternalStore(
    (fn) => subscribeSync(() => fn()),
    () => currentSyncState,
    () => currentSyncState,
  );
}

let currentSyncState: SyncState = {
  status: "idle",
  pending: 0,
  conflicts: 0,
  deadLetters: 0,
  cursor: 0,
  lastSyncAt: null,
  lastError: null,
};
subscribeSync((s) => {
  currentSyncState = s;
});

// ---------------------------------------------------------------------------
// Route table
// ---------------------------------------------------------------------------

function NotFound(): ReactNode {
  return (
    <div className="pb-empty">
      <div className="pb-label">404</div>
      <p>No such page.</p>
      <Link to="/" className="pb-btn">
        Back to the table
      </Link>
    </div>
  );
}

/**
 * The whole table, in one place:
 *
 *     /                       catalog   Home
 *     /catalog                catalog   Catalog
 *     /game/:id               catalog   GameDetail
 *     /setup                  session   Setup
 *     /session/:id            session   Rating
 *     /session/:id/results    session   Results
 *     /join/:code             session   Join
 *     /settings               settings  Settings
 *     /settings/aspects       settings  Aspects
 *     /settings/profiles      settings  Profiles
 *     /settings/sync          settings  SyncStatus
 *
 * Registration order does not matter here: `match` requires an equal segment
 * count and every pattern of a given length differs in a literal segment, so no
 * two of these can ever claim the same path. The near-miss is `/session/:id`,
 * which would swallow a sibling like `/session/new` — hence Setup lives at
 * `/setup`. Keep that property when adding a route; the first match wins and a
 * shadowed pattern fails silently.
 */
registerRoutes({ ...catalogRoutes, ...sessionRoutes, ...settingsRoutes });
setNotFoundScreen(NotFound);

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

/**
 * Shown for the one round trip it takes to learn whether there is a session.
 *
 * Deliberately almost nothing. The two failure modes worth avoiding are a flash
 * of the front door in front of a returning user and a flash of an empty app in
 * front of a stranger, and both come from guessing during this gap rather than
 * waiting it out. So: the wordmark, and no claim either way.
 */
function Booting(): ReactNode {
  return (
    <main className="pb-app pb-boot" role="status" aria-live="polite">
      <div className="pb-boot__mark">Punchboard</div>
      <div className="pb-boot__line" />
      <span className="pb-boot__say">Finding your table…</span>
    </main>
  );
}

/**
 * The gate.
 *
 * Screens read from IndexedDB rather than from the server, so without this an
 * anonymous visitor got the whole app rendered and empty, every write 401ing in
 * silence and no way to sign in anywhere on screen. The decision itself lives in
 * `auth/gate.ts` — it is pure, and `/join/:code` staying reachable without an
 * account is a promise worth testing directly rather than inferring from a tree.
 */
export function App(): ReactNode {
  const { path } = useRoute();
  const { status } = useAuthSession();

  switch (chooseView(status, path)) {
    case "loading":
      return <Booting />;
    case "front-door":
      return <SignedOut />;
    default:
      return (
        <main className="pb-app">
          <Routes />
        </main>
      );
  }
}

function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  // The worker source is `src/client/sw.ts`; the server builds it to `/sw.js`.
  // Failure here is not fatal — it costs offline launch, not the app.
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((err) => {
      console.warn("[punchboard] service worker not registered:", err);
    });
  });
}

export function boot(): void {
  const container = document.getElementById("root");
  if (!container) throw new Error("#root missing from index.html");
  applyLang();
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
  registerServiceWorker();
  // The session is resolved once, here, rather than per route: a hook that
  // fetched on mount would hit `/get-session` on every navigation.
  //
  // Sync lives outside React for the same reason it always did — it must keep
  // running across route changes and must not be restarted by StrictMode's
  // double effect invocation — but it now waits for an account. Starting it for
  // an anonymous visitor bought two guaranteed 401s before first paint and
  // paused an outbox that had nothing in it. `session.ts` owns that call now —
  // both here, once the session resolves to a user, and again on sign-in.
  void resolveSession();
}

// Guarded so the module can be imported outside a browser (tests import `Link`
// and the router helpers); in the browser this is an unconditional boot.
if (typeof document !== "undefined") boot();
